import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Loader2,
  Clock,
  Trash2,
  Timer
} from 'lucide-react';
import { formatDateDisplay } from '../../utils/dateUtils';
import { BulkTimeAdjustDialog, BulkScheduleSlotToggleDialog } from '../../components/BulkOperationsDialogs';

const ScheduleSetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scheduleSet, setScheduleSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeAdjustDialogOpen, setTimeAdjustDialogOpen] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bulkToggleDialogOpen, setBulkToggleDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await api.get(`/schedule-sets/${id}`);
      setScheduleSet(response.data);
    } catch (error) {
      toast.error('Failed to fetch schedule set');
      navigate('/schedule-sets');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSlot = async (slotId, isActive) => {
    try {
      await api.patch(`/schedule-sets/${id}/slots/${slotId}?is_active=${isActive}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await api.delete(`/schedule-sets/${id}/slots/${slotId}`);
      toast.success('Slot removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete slot');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const activeSlots = scheduleSet?.time_slots?.filter(s => s.is_active).length || 0;
  const totalSlots = scheduleSet?.time_slots?.length || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="schedule-set-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/schedule-sets')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900">{scheduleSet?.friendly_name}</h1>
              <p className="text-slate-500">{formatDateDisplay(scheduleSet?.conference_date)} · {scheduleSet?.school_level_description}</p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => setTimeAdjustDialogOpen(true)}
            data-testid="bulk-time-adjust-btn"
          >
            <Timer className="h-4 w-4 mr-2" />
            Adjust All Times
          </Button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Date</p>
              <p className="text-lg font-semibold">{formatDateDisplay(scheduleSet?.conference_date)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Time Range</p>
              <p className="text-lg font-semibold">{scheduleSet?.start_time} - {scheduleSet?.end_time}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Duration/Break</p>
              <p className="text-lg font-semibold">{scheduleSet?.duration_minutes}min / {scheduleSet?.break_minutes}min</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active Slots</p>
              <p className="text-lg font-semibold">{activeSlots} / {totalSlots}</p>
            </CardContent>
          </Card>
        </div>

        {/* Attendance Option */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Default Attendee Option</p>
                <p className="text-sm text-slate-500">How attendees can join conferences. You can override this for individual teachers in the Export Set.</p>
              </div>
              <Badge variant="default">{scheduleSet?.attendance_option}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Time Slots */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">Time Slots</CardTitle>
            <CardDescription>
              Toggle slots on/off or remove unwanted slots
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scheduleSet?.time_slots?.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No time slots generated</p>
              </div>
            ) : (
              <>
                {/* Bulk Actions Bar */}
                {selectedSlots.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 p-3 mb-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <span className="text-sm font-medium text-indigo-700">
                      {selectedSlots.length} slot(s) selected
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setBulkToggleDialogOpen(true)}
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                      data-testid="bulk-toggle-slots-btn"
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Enable/Disable
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedSlots([])}
                      className="text-slate-500"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedSlots.length === scheduleSet?.time_slots?.length && scheduleSet?.time_slots?.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSlots(scheduleSet?.time_slots?.map((_, idx) => idx) || []);
                            } else {
                              setSelectedSlots([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Start Time</TableHead>
                      <TableHead>End Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleSet?.time_slots?.map((slot, index) => (
                      <TableRow key={slot.id} className={!slot.is_active ? 'opacity-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedSlots.includes(index)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedSlots([...selectedSlots, index]);
                              } else {
                                setSelectedSlots(selectedSlots.filter(i => i !== index));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{slot.start_time}</TableCell>
                        <TableCell>{slot.end_time}</TableCell>
                        <TableCell>
                          <Badge variant={slot.is_active ? 'default' : 'outline'}>
                            {slot.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                          checked={slot.is_active}
                          onCheckedChange={(checked) => handleToggleSlot(slot.id, checked)}
                          data-testid={`toggle-slot-${slot.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600"
                          onClick={() => handleDeleteSlot(slot.id)}
                          data-testid={`delete-slot-${slot.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </>
            )}
          </CardContent>
        </Card>

        {/* Bulk Time Adjust Dialog */}
        <BulkTimeAdjustDialog
          open={timeAdjustDialogOpen}
          onOpenChange={setTimeAdjustDialogOpen}
          scheduleSetId={id}
          onSuccess={fetchData}
        />

        {/* Bulk Slot Toggle Dialog */}
        <BulkScheduleSlotToggleDialog
          open={bulkToggleDialogOpen}
          onOpenChange={setBulkToggleDialogOpen}
          scheduleSetId={id}
          selectedSlots={selectedSlots}
          onSuccess={() => {
            setSelectedSlots([]);
            fetchData();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default ScheduleSetDetailPage;
